/**
 * SADLLicenseCard — renders decoded driver's license data as a
 * license-card-style visual instead of a datagrid.
 *
 * The embedded photo uses the proprietary Summus 'WI' wavelet format which
 * cannot be decoded in-browser — a placeholder icon is shown instead.
 * An "EXPIRED" stamp overlays the card when the license validity date
 * has passed.
 */

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/security-services/guard-mobile.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/security-services/guard-mobile)
 */

import React from 'react';
import { User } from 'lucide-react';
import type { SADLData } from '@/lib/sadl/sadlDecoder';

function isLicenseExpired(isoDate: string | null): boolean {
  if (!isoDate) return false;
  const expiry = new Date(`${isoDate}T23:59:59`);
  return !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now();
}

function CardField({
  label,
  value,
  mono,
  span2,
  light,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  span2?: boolean;
  light?: boolean;
}) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <div
        className={`text-[9px] uppercase tracking-wider ${
          light ? 'text-emerald-700/70 dark:text-emerald-400/60' : 'text-gray-400 dark:text-gray-500'
        }`}
      >
        {label}
      </div>
      <div
        className={`text-[13px] leading-tight font-semibold truncate ${
          mono ? 'font-mono' : ''
        } ${light ? 'text-emerald-950 dark:text-emerald-50' : 'text-gray-800 dark:text-gray-200'}`}
      >
        {value || '—'}
      </div>
    </div>
  );
}

export default function SADLLicenseCard({ sadl }: { sadl: SADLData }) {
  const expired = isLicenseExpired(sadl.licenseValidityExpiry);
  const codes = sadl.vehicleLicenses.map((v) => v.code).filter(Boolean).join(' ');
  const gender =
    sadl.gender === '01' ? 'Male' : sadl.gender === '02' ? 'Female' : null;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-emerald-200 dark:border-emerald-800/60 shadow-sm select-none">
      {/* Card header — SA green band */}
      <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 px-4 py-2.5 flex items-center justify-between">
        <div>
          <div className="text-[8px] uppercase tracking-[0.25em] text-emerald-100/80">
            Republic of South Africa
          </div>
          <div className="text-sm font-black tracking-wider text-white uppercase">
            Driving Licence
          </div>
        </div>
        {codes && (
          <div className="text-right">
            <div className="text-[8px] uppercase tracking-[0.25em] text-emerald-100/80">
              Codes
            </div>
            <div className="font-mono text-sm font-bold text-white tracking-widest">
              {codes}
            </div>
          </div>
        )}
      </div>

      {/* Card body */}
      <div className="bg-gradient-to-br from-emerald-50/80 via-white to-emerald-50/50 dark:from-emerald-950/30 dark:via-gray-800 dark:to-emerald-950/20">
        <div className="flex gap-3 p-3.5">
          {/* Photo placeholder */}
          <div className="w-[72px] h-[90px] flex-shrink-0 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 flex flex-col items-center justify-center overflow-hidden">
            <User className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            {sadl.photoWidth && sadl.photoHeight && (
              <span className="text-[7px] text-gray-400 dark:text-gray-500 mt-1">
                {sadl.photoWidth}×{sadl.photoHeight}
              </span>
            )}
          </div>

          {/* Primary fields */}
          <div className="flex-1 min-w-0 grid grid-cols-2 gap-x-3 gap-y-2 content-start">
            <CardField label="Surname" value={sadl.surname} light span2 />
            <CardField label="Initials" value={sadl.initials} light />
            <CardField label="Gender" value={gender} light />
            <CardField label="ID Number" value={sadl.idNumber} mono light span2 />
            <CardField label="Licence No" value={sadl.licenseNumber} mono light span2 />
          </div>
        </div>

        {/* Dates strip */}
        <div className="grid grid-cols-3 gap-x-3 px-3.5 py-2.5 border-t border-emerald-100 dark:border-emerald-900/50">
          <CardField label="Date of Birth" value={sadl.birthDate} mono light />
          <CardField label="Valid From" value={sadl.licenseValidityStart} mono light />
          <CardField label="Valid Until" value={sadl.licenseValidityExpiry} mono light />
        </div>

        {/* Secondary fields — only rendered if present */}
        {(sadl.driverRestrictions ||
          sadl.licenseIssueNumber ||
          sadl.professionalDrivingPermitExpiry ||
          sadl.professionalDrivingPermitCodes.length > 0) && (
          <div className="grid grid-cols-4 gap-x-3 px-3.5 py-2 border-t border-emerald-100 dark:border-emerald-900/50">
            <CardField label="Issue" value={sadl.licenseIssueNumber} mono light />
            <CardField label="Restr." value={sadl.driverRestrictions} mono light />
            <CardField label="PrDP Exp" value={sadl.professionalDrivingPermitExpiry} mono light />
            <CardField
              label="PrDP"
              value={sadl.professionalDrivingPermitCodes.join(' ') || null}
              mono
              light
            />
          </div>
        )}
      </div>

      {/* EXPIRED stamp */}
      {expired && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="rotate-[-12deg] rounded-lg border-4 border-red-600/90 bg-white/60 dark:bg-gray-900/60 px-8 py-2 backdrop-blur-[1px] shadow-lg">
            <span className="text-4xl font-black uppercase tracking-[0.2em] text-red-600/90">
              Expired
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
